// src/schema/product.js
import { z } from "zod";

// URL http/https OU caminho local /assets
const urlOrAssetPath = z
  .string()
  .trim()
  .refine(
    (v) =>
      /^https?:\/\/.+/i.test(v) ||
      /^\/assets\/.+/i.test(v) ||
      /^\/?.+\.(png|jpg|jpeg|webp|gif)$/i.test(v),
    "Informe uma URL válida (http/https) ou caminho relativo de imagem"
  );

const ingredientItem = z.object({
  id: z.string().min(1, "id inválido"),
  name: z.string().min(1, "name obrigatório"),
  price: z.coerce.number().min(0, "price ≥ 0"),
});

// Schema principal para o formulário
export const productFormSchema = z.object({
  name: z.string().trim().min(1, "Nome obrigatório"),
  description: z.string().optional().default(""),
  category: z.string().trim().min(1, "Categoria obrigatória"),
  price: z.coerce
    .number({ invalid_type_error: "Preço deve ser numérico" })
    .min(0, "Preço ≥ 0"),
  image_url: urlOrAssetPath,
  available: z.boolean().default(true),

  // checkbox
  creatingFlag: z.boolean().default(false),
  hasExtraProductsFlag: z.boolean().default(false),
  hasExtraIngredientsFlag: z.boolean().default(false),
  combo: z.boolean().default(false),

  // campos condicionais
  unid: z.string().optional().default(""),
  weight: z.coerce.number().int().nonnegative().optional().default(0),
  unid_product: z.string().optional().default(""),
  weight_product: z.coerce.number().int().nonnegative().optional().default(0),
  totalIngredients: z.coerce.number().int().nonnegative().optional().default(0),

  totalProductsSelects: z.coerce
    .number()
    .int()
    .nonnegative()
    .optional()
    .default(0),

  min_stock: z.coerce.number().int().nonnegative().optional().default(0),

  // ingredientes selecionados
  ingredientsSelectedIds: z.array(z.string()).optional().default([]),
  allIngredientsCatalog: z.array(ingredientItem).optional().default([]),
  productsSelectedCodes: z.array(z.string()).optional().default([]),
});

// Converter os valores do form em payload para a API
export function toProductPayload(formValues) {
  const creating =
    formValues.creatingFlag || formValues.hasExtraProductsFlag ? "S" : "N";

  const mapById = new Map(
    (formValues.allIngredientsCatalog || []).map((i) => [String(i.id), i])
  );
  const ingredients = (formValues.ingredientsSelectedIds || [])
    .map((id) => mapById.get(String(id)))
    .filter(Boolean);

  if (creating === "N") {
    return {
      name: formValues.name,
      description: formValues.description || "",
      category: formValues.category,
      price: formValues.price,
      image_url: formValues.image_url,
      available: formValues.available ?? true,
      creating: "N",
      unid: "",
      weight: 0,
      unid_product: "",
      weight_product: 0,
      totalIngredients: 0,
      min_stock: formValues.min_stock || 0,
      ingredients, // pode ser []
    };
  }

  const products = (formValues.productsSelectedCodes || [])
    .map((id) => mapById.get(String(id)))
    .filter(Boolean);

  return {
    name: formValues.name,
    description: formValues.description || "",
    category: formValues.category,
    price: formValues.price,
    image_url: formValues.image_url,
    available: formValues.available ?? true,
    creating: "S",
    unid: (formValues.unid || "").trim(),
    weight: formValues.weight,
    unid_product: (formValues.unid_product || "").trim(),
    weight_product: formValues.weight_product,
    totalIngredients: formValues.totalIngredients,
    hasExtraProducts: formValues.hasExtraProductsFlag ? "S" : "N",
    hasExtraIngredients: formValues.hasExtraIngredientsFlag ? "S" : "N",
    totalProductsSelects: formValues.totalProductsSelects,
    combo: formValues.combo ? "S" : "N",
    min_stock: formValues.min_stock || 0,
    ingredients,
    products,
  };
}
